import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Activity, CreditCard, Droplets, Leaf, RefreshCw,
  Repeat, Trash2, Users, Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import StickyFilterBar from './components/filters/StickyFilterBar';
import PremiumKpiCard from './components/kpi/PremiumKpiCard';
import { AnalyticsChartCard } from './components/analytics/AnalyticsChartCard';
import { ScopeBreakdownCard } from './components/analytics/ScopeBreakdownCard';
import { useIntensityData } from './hooks/useIntensityData';
import { ExportPDFButton } from './pdf-export';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH = 'monthly';

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

const aggregateSeries = (rows, granularity, keys) => {
  if (granularity === MONTH) return rows;
  const groups = new Map();
  rows.forEach((row) => {
    const month = Number(row.period?.slice(5, 7));
    const group =
      granularity === 'quarterly'
        ? `${row.period.slice(0, 4)} Q${Math.ceil(month / 3)}`
        : row.period?.slice(0, 4);
    const current = groups.get(group) || { period: group, count: 0 };
    keys.forEach((k) => { current[k] = (current[k] || 0) + Number(row[k] || 0); });
    current.count += 1;
    groups.set(group, current);
  });
  const ratioKeys = new Set(
    keys.filter((k) =>
      k.toLowerCase().includes('pct') ||
      ['turnover', 'ltifr', 'apDays', 'cashConversion'].includes(k),
    ),
  );
  return Array.from(groups.values()).map((row) => ({
    ...row,
    ...Object.fromEntries(
      [...ratioKeys].map((k) => [k, row[k] / row.count]),
    ),
  }));
};

const percentageSeries = (rows, numerator, denominator, output) =>
  rows.map((row) => ({
    ...row,
    [output]: row[denominator] ? (row[numerator] / row[denominator]) * 100 : 0,
  }));

// ---------------------------------------------------------------------------
// Chart row configurations
// ---------------------------------------------------------------------------

const EMISSION_SERIES_BASE = [
  // { key: 'scope1', label: 'Scope 1', color: '#15803D' },
  // { key: 'scope2', label: 'Scope 2', color: '#2563EB' },
  // { key: 'scope3', label: 'Scope 3', color: '#7C3AED' },
  { key: 'scope1', label: 'Scope 1', color: '#10B981' },
  { key: 'scope2', label: 'Scope 2', color: '#3B82F6' },
  { key: 'scope3', label: 'Scope 3', color: '#8B5CF6' },
];

const ENERGY_SERIES = [
  { key: 'renewable', label: 'Renewable', color: '#65A30D' },
  { key: 'nonRenewable', label: 'Non-renewable', color: '#F97316' },
];

const WATER_SERIES = [
  { key: 'withdrawn', label: 'Withdrawn', color: '#0284C7' },
  { key: 'consumed', label: 'Consumed', color: '#0EA5E9' },
  { key: 'discharged', label: 'Discharged', color: '#64748B' },
  { key: 'recycled', label: 'Recycled', color: '#14B8A6' },
];

const WASTE_SERIES = [
  { key: 'generated', label: 'Generated', color: '#57534E' },
  { key: 'recovered', label: 'Recovered', color: '#65A30D' },
  { key: 'disposed', label: 'Disposed', color: '#EF4444' },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ExecutiveAnalyticsDashboard({ data }) {
  const { getAuthHeader } = useAuth();
  const {
    stats, loading, organization, facilities,
    selectedFacilities, setSelectedFacilities,
    dateRange, setDateRange,
    showFilters, setShowFilters,
    showFacilityDropdown, setShowFacilityDropdown, facilityDropdownRef,
    filteredData, isLive, getCurrentFinancialYear,
  } = data;

  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [granularity, setGranularity] = useState(MONTH);
  const [activeScopes, setActiveScopes] = useState([]);
  const { productionQty, productionUnit } = useIntensityData(dateRange, selectedFacilities);

  // --------------- Data fetching ---------------

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;
    const load = async () => {
      setAnalyticsLoading(true);
      const params = {
        start_date: format(dateRange.from, 'yyyy-MM'),
        end_date: format(dateRange.to, 'yyyy-MM'),
        facility_ids: selectedFacilities.length ? selectedFacilities.join(',') : undefined,
      };
      const [analyticsRes, metricsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/dashboard/esg-analytics`, { headers: getAuthHeader(), params }).catch(() => ({ data: null })),
        axios.get(`${API}/esg-records/dashboard-metrics`, { headers: getAuthHeader(), params }).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/esg-summary`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
      ]);
      setAnalytics(analyticsRes.data);
      setMetrics(metricsRes.data);
      setSummary(summaryRes.data);
      setAnalyticsLoading(false);
    };
    load();
  }, [dateRange, selectedFacilities, getAuthHeader]);

  // --------------- Derived KPI values ---------------

  const totals = filteredData?.totals || {};
  const emissionData = metrics?.emissions?.ghg_emissions || {};
  const scope12 = (emissionData.total_scope1 || 0) + (emissionData.total_scope2 || 0);
  const totalEmissions = emissionData.total ?? totals.total ?? 0;
  const totalEnergy = metrics?.energy?.total || 0;
  const energyIntensity = productionQty ? totalEnergy / productionQty : null;
  const ghgIntensity = productionQty ? scope12 / productionQty : null;


  const scopeTotals = {
    scope1: totals.scope1 || emissionData.total_scope1 || 0,
    scope2: totals.scope2 || emissionData.total_scope2 || 0,
    scope3: totals.scope3 || emissionData.total_scope3 || 0,
    biogenic: totals.biogenic || emissionData.total_biogenic || 0,
  };

  // --------------- Chart data series ---------------

  const analyticsData = analytics || {
    emissions: [], energy: [], water: [], waste: [],
    workforce: [], safety: [], finance: [], breaches: [],
    governance: {},
  };
  console.log("analyticsData", analyticsData)
  const emissionRows = aggregateSeries(analyticsData.emissions, granularity, ['scope1', 'scope2', 'scope3'])
    .map((row) => ({
      ...row,
      totalEmissions: Number(row.scope1 || 0) + Number(row.scope2 || 0) + Number(row.scope3 || 0),
    }));
  const energyRows = aggregateSeries(analyticsData.energy, granularity, ['renewable', 'nonRenewable']);
  const waterRows = aggregateSeries(analyticsData.water, granularity, ['withdrawn', 'consumed', 'discharged', 'recycled']);
  const wasteRows = aggregateSeries(analyticsData.waste, granularity, ['generated', 'recovered', 'disposed']);
  const workforceRows = aggregateSeries(analyticsData.workforce, granularity, ['employees', 'turnover', 'ltifr', 'lostTimeInjuries']);
  const safetyRows = aggregateSeries(analyticsData.safety, granularity, ['fatalities', 'lostTimeInjuries', 'nearMisses']);
  const financeRows = aggregateSeries(analyticsData.finance, granularity, ['apDays', 'aging0to30', 'aging31to60', 'aging61to90', 'agingOver90', 'cashConversion']);
  const breachRows = aggregateSeries(analyticsData.breaches, granularity, ['breaches', 'confidentiality', 'integrity', 'availability', 'privacy']);

  const renewableIntensityRows = energyRows.map((row) => ({
    ...row,
    renewablePct: row.renewable + row.nonRenewable
      ? (row.renewable / (row.renewable + row.nonRenewable)) * 100
      : 0,
    energyIntensity: productionQty ? (row.renewable + row.nonRenewable) / productionQty : 0,
  }));
  const incidentsRows = aggregateSeries(analyticsData.incidents || [], granularity, ['dataBreaches', 'healthSafety', 'violations']);

  const emissionSeries = [
    ...EMISSION_SERIES_BASE.filter((s) => !activeScopes.length || activeScopes.includes(s.key)),
    { key: 'totalEmissions', label: 'Total Emissions', color: '#334155' },
  ];

  // --------------- Interaction helpers ---------------

  const toggleScope = (scope) =>
    setActiveScopes((cur) => cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]);

  const filterProps = {
    dateRange, setDateRange, facilities, selectedFacilities, setSelectedFacilities,
    showFacilityDropdown, setShowFacilityDropdown, facilityDropdownRef, getCurrentFinancialYear,
  };

  // --------------- Render ---------------

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="executive-esg-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  console.log("waterRows", waterRows)
  console.log("metrics", metrics)
  console.log("wasteRows", wasteRows)

  return (
    <div className="w-full max-w-full min-w-0 space-y-6 overflow-x-visible pb-10" data-testid="executive-esg-dashboard">

      {/* ── Header / Filter Bar ── */}
      <StickyFilterBar
        title={organization?.name ? `${organization.name} · Executive ESG Dashboard` : 'Executive ESG Dashboard'}
        organizationLogo={organization?.logo}
        organizationName={organization?.name}
        subtitle={dateRange.from && dateRange.to ? `Reporting window: ${format(dateRange.from, 'MMM yyyy')} – ${format(dateRange.to, 'MMM yyyy')}` : 'Reporting window'}
        liveBadge={isLive ? <span className="text-[10px] font-semibold text-emerald-700">LIVE</span> : null}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterProps={filterProps}
        showExport
        exportButton={
          <ExportPDFButton
            organization={organization}
            dateRange={dateRange}
            metrics={metrics}
            analytics={analytics}
            summary={summary}
            filteredData={filteredData}
            granularity={granularity}
            productionUnit={productionUnit || 'unit'}
            productionQty={productionQty}
          />
        }
        dashboardType={data.dashboardType}
        setDashboardType={data.setDashboardType}
        esgSection={data.esgSection}
        setEsgSection={data.setEsgSection}
        showDashboardToggle={data.showDashboardToggle}
      />

      {/* ── Row 1: Top KPI Cards ── */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5" data-testid="top-kpi-row">
        <PremiumKpiCard title="Total Emissions" value={totalEmissions} unit="tCO₂e" icon={Leaf} accentColor="#15803D" loading={analyticsLoading} />
        <PremiumKpiCard title="GHG Intensity (Production)" value={ghgIntensity} unit={`tCO₂e/${productionUnit || 'unit produced'}`} icon={Leaf} accentColor="#0F766E" loading={analyticsLoading} />
        <PremiumKpiCard title="Energy Intensity (Production)" value={energyIntensity} unit={`MWh/${productionUnit || 'unit produced'}`} icon={Zap} accentColor="#F59E0B" loading={analyticsLoading} />
        <PremiumKpiCard title="Renewable Energy" value={metrics?.energy?.renewable_pct} unit="%" icon={Zap} accentColor="#84CC16" loading={analyticsLoading} />
        <PremiumKpiCard title="Water Recycled" value={metrics?.water?.recycled} unit="KL" icon={Droplets} accentColor="#0284C7" loading={analyticsLoading} />
        <PremiumKpiCard title="Waste Recovery" value={metrics?.waste?.recovered} unit="MT" icon={Trash2} accentColor="#57534E" loading={analyticsLoading} />
        <PremiumKpiCard
          title="Employees" value={summary?.kpis?.total_employees?.value}
          secondaryLabel="Female" secondaryValue={summary?.kpis?.diversity_pct?.value}
          secondaryUnit="%" secondaryTestId="kpi-employees-female-workforce"
          unit="" icon={Users} accentColor="#7C3AED"
        />
        <PremiumKpiCard title="LTIFR" value={summary?.kpis?.ltifr?.value} unit="" icon={Activity} accentColor="#DC2626" />
        <PremiumKpiCard title="Accounts Payable Days" value={summary?.kpis?.ap_days?.value} unit="days" icon={CreditCard} accentColor="#4F46E5" />
        <PremiumKpiCard title="Employee Turnover" value={summary?.kpis?.turnover_pct?.value} unit="%" icon={Repeat} accentColor="#F97316" />
      </div>

      {/* ── Row 2: GHG Emissions + Scope Breakdown ── */}
      <div className="grid min-w-0 grid-cols-1 items-stretch gap-6 xl:grid-cols-12">
        
        {/* Left Chart (8 columns) */}
        <div className="flex min-w-0 flex-col xl:col-span-8">
          <AnalyticsChartCard 
            className="h-full flex-1" 
            title="GHG Emission Trend" 
            subtitle="Scope emissions and total-emissions trend" 
            data={emissionRows} 
            series={emissionSeries} 
            chartType="area" 
            accent="#15803D" 
            unit="tCO₂e" 
            yAxisLabel="tCO₂e"
            testId="ghg-emission-trend" 
            loading={analyticsLoading} 
            // Pass the toggle inside the card using a custom prop
            headerAction={
              <div className="flex gap-1" data-testid="emission-granularity-toggle">
                {[['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['yearly', 'Yearly']].map(([value, label]) => (
                  <button
                    type="button" key={value}
                    onClick={() => setGranularity(value)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${granularity === value ? 'bg-[#1A4D2E] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                    data-testid={`emission-granularity-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />
        </div>

        {/* Right Summary (4 columns) */}
        <div className="flex min-w-0 flex-col xl:col-span-4">
          <ScopeBreakdownCard 
            className="h-full flex-1" 
            totals={scopeTotals} 
            activeScopes={activeScopes} 
            onToggleScope={toggleScope} 
          />
        </div>

      </div>

      {/* ── Row 3: Energy ── */}
      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
        <AnalyticsChartCard title="Energy Mix" subtitle="Renewable and non-renewable consumption" data={energyRows} series={ENERGY_SERIES} chartType="bar" stacked accent="#F97316" unit="MWh" yAxisLabel="MWh" testId="energy-mix-chart" loading={analyticsLoading} />
        <AnalyticsChartCard title="Renewable % & Energy Intensity" subtitle="Renewable share (left axis) and energy intensity (right axis)" data={renewableIntensityRows} series={[{ key: 'renewablePct', label: 'Renewable %', color: '#65A30D', yAxisId: 'left', unit: '%', axisLabel: 'Renewable %' }, { key: 'energyIntensity', label: 'Energy Intensity', color: '#F97316', yAxisId: 'right', unit: `MWh/${productionUnit || 'unit'}`, axisLabel: `MWh/${productionUnit || 'unit'}` }]} accent="#65A30D" testId="renewable-intensity-trend" loading={analyticsLoading} />
      </div>

      {/* ── Row 4: Water & Waste ── */}
      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
        <AnalyticsChartCard title="Water Flow" subtitle="Water volumes by operational stage" data={waterRows} series={WATER_SERIES} chartType="area" accent="#0284C7" unit="KL" yAxisLabel="KL" testId="water-flow-chart" loading={analyticsLoading} />
        <AnalyticsChartCard title="Waste Management" subtitle="Generated, recovered, and disposed" data={wasteRows} series={WASTE_SERIES} chartType="area" accent="#57534E" unit="MT" yAxisLabel="MT" testId="waste-management-chart" loading={analyticsLoading} />
      </div>

      {/* ── Row 5: LTIFR & AP Days ── */}
      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
        <AnalyticsChartCard title="LTIFR Trend" subtitle="Lost-time injury frequency rate" data={workforceRows} series={[{ key: 'ltifr', label: 'LTIFR', color: '#DC2626' }]} accent="#DC2626" testId="ltifr-trend-chart" loading={analyticsLoading} />
        <AnalyticsChartCard title="Accounts Payable Days" subtitle="Days payable outstanding" data={financeRows} series={[{ key: 'apDays', label: 'AP Days', color: '#4F46E5' }]} accent="#4F46E5" unit="days" testId="ap-days-chart" loading={analyticsLoading} />
      </div>

      {/* ── Row 6: Incidents Trend ── */}
      <div className="grid min-w-0 grid-cols-1 gap-6">
        <AnalyticsChartCard title="Incidents Trend" subtitle="Data breaches, health & safety incidents, and violations over time" data={incidentsRows} series={[{ key: 'dataBreaches', label: 'Data Breaches', color: '#DC2626' }, { key: 'healthSafety', label: 'Health & Safety', color: '#F97316' }, { key: 'violations', label: 'Violations', color: '#7C3AED' }]} chartType="bar" accent="#DC2626" testId="incidents-trend-chart" loading={analyticsLoading} />
      </div>

    </div>
  );
}
